/**
 * ReservationsNew — inserimento rapido prenotazioni (telefono, WhatsApp, ...).
 * iPhone: colonna unica + conferma sticky. iPad/desktop: due colonne.
 * L'operatore è derivato SOLO lato server dalla sessione PIN.
 */
import React, { useEffect, useRef, useState } from "react";
import { bookingApi } from "@/lib/reservations";
import { formatApiError } from "@/lib/api";
import BookingCustomerCard from "./ReservationsCustomerCard";
import BookingSlotIndicator from "./ReservationsSlotIndicator";
import ReservationsTimePicker, { TIME_RE } from "./ReservationsTimePicker";
import OriginPicker from "./ReservationsOriginPicker";
import AmbiguousPicker from "./ReservationsAmbiguousPicker";
import WhatsAppConfirmToggle from "@/components/reservations/WhatsAppConfirmToggle";
import ZonePicker from "@/components/reservations/ZonePicker";
import { toast } from "sonner";
import { Minus, Plus, Loader2, Check } from "lucide-react";

const romeISO = (d = new Date()) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome" }).format(d);
const todayISO = () => romeISO();
const addDays = (n) => {
  const d = new Date(todayISO() + "T12:00:00"); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const romeNowHM = () => new Intl.DateTimeFormat("it-IT", {
  timeZone: "Europe/Rome", hour: "2-digit", minute: "2-digit", hour12: false,
}).format(new Date());
const humanDateShort = (iso) => (iso ? new Date(iso + "T12:00").toLocaleDateString("it-IT",
  { weekday: "short", day: "2-digit", month: "short" }) : "");
const shortDayNum = (iso) => {
  if (!iso) { return ""; }
  const d = new Date(iso + "T12:00");
  const wd = d.toLocaleDateString("it-IT", { weekday: "short" }).replace(".", "").toUpperCase().slice(0, 3);
  return `${wd} ${d.toLocaleDateString("it-IT", { day: "2-digit" })}`;
};

const PREFILL_KEYS = ["customer_name", "customer_surname", "customer_phone", "pax", "date", "time", "notes", "booking_source"];
// La richiesta WhatsApp e la zona non vengono MAI ereditate (nuovo form, duplicazione).
const EMPTY = (prefill) => {
  const base = { customer_name: "", customer_surname: "", customer_phone: "",
    pax: 2, date: todayISO(), time: "20:30", notes: "", booking_source: "phone", customer_id: null };
  PREFILL_KEYS.forEach((k) => { if (prefill?.[k] !== undefined && prefill?.[k] !== null && prefill?.[k] !== "") { base[k] = prefill[k]; } });
  if (base.date < todayISO()) { base.date = todayISO(); }
  return { ...base, zone: "Ristorante", send_whatsapp: false };
};

const messageLine = (m) => {
  if (!m?.requested) { return ""; }
  if (m.status === "accepted" || m.status === "replayed") { return "Conferma WhatsApp richiesta: esito in attesa da Meta"; }
  if (m.status === "unavailable") { return "WhatsApp non inviato: servizio non attivo"; }
  return "WhatsApp non inviato (errore): prenotazione salvata";
};

const inputCls = "w-full h-12 px-3 rounded-xl bg-white border border-sand-300 text-base text-ink-900 " +
  "focus:outline-none focus:border-bronze-500 focus:ring-2 focus:ring-bronze-400/30 transition-colors";

function Label({ children }) {
  return <label className="block text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">{children}</label>;
}

function Card({ children, className = "" }) {
  return <section className={`rounded-2xl bg-sand-50 border border-sand-200 p-3 sm:p-4 space-y-3 ${className}`}>{children}</section>;
}


function PaxStepper({ value, onChange }) {
  const n = Number.isInteger(value) ? value : 0;
  const btn = "w-11 h-11 rounded-full bg-white border border-sand-300 flex items-center justify-center active:scale-95 transition-transform";
  return (
    <div className="flex items-center justify-between rounded-xl bg-white border border-sand-300 px-1.5 h-14">
      <button type="button" onClick={() => onChange(Math.max(1, (n || 1) - 1))} data-testid="booking-pax-minus" className={btn} aria-label="Meno coperti">
        <Minus className="w-5 h-5" />
      </button>
      <input type="number" inputMode="numeric" pattern="[0-9]*" min={1} step={1}
             value={Number.isInteger(value) ? value : ""} aria-label="Coperti" data-testid="booking-pax"
             onChange={(e) => {
               const v = e.target.value;
               if (v === "") { onChange(""); return; }
               const k = parseInt(v, 10);
               if (Number.isFinite(k) && k >= 1) { onChange(k); }
             }}
             className="w-20 text-center text-3xl font-semibold tabular-nums bg-transparent border-none outline-none appearance-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
      <button type="button" onClick={() => onChange(n + 1)} data-testid="booking-pax-plus" className={btn} aria-label="Più coperti">
        <Plus className="w-5 h-5" />
      </button>
    </div>
  );
}

function DatePicker({ value, onChange }) {
  const quick = [0, 1, 2].map(addDays);
  const isCustom = !quick.includes(value);
  const chip = (active) => `h-14 rounded-xl border px-1 flex flex-col items-center justify-center leading-tight transition-colors ${
    active ? "bg-ink-900 text-white border-ink-900" : "bg-white border-sand-300 text-ink-900 hover:border-bronze-400"}`;
  return (
    <div className="grid grid-cols-4 gap-2">
      {quick.map((iso, n) => (
        <button key={iso} type="button" onClick={() => onChange(iso)} className={chip(value === iso)}
                data-testid={`booking-date-${n === 0 ? "today" : n === 1 ? "tomorrow" : "day2"}`}>
          <span className="font-semibold text-sm">{n === 0 ? "OGGI" : n === 1 ? "DOMANI" : shortDayNum(iso)}</span>
          <span className="text-[11px] opacity-70">{humanDateShort(iso)}</span>
        </button>
      ))}
      <label className={`${chip(isCustom)} relative cursor-pointer`} data-testid="booking-date-custom">
        <span className="font-semibold text-sm">{isCustom ? shortDayNum(value) : "ALTRA"}</span>
        <span className="text-[11px] opacity-70">{isCustom ? humanDateShort(value) : "data"}</span>
        <input type="date" value={value} min={todayISO()} onChange={(e) => e.target.value && onChange(e.target.value)}
               aria-label="Scegli data" data-testid="booking-date-input"
               className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
      </label>
    </div>
  );
}

export default function BookingNew({ prefill = null, entryPoint = "reservations", onSaved = null }) {
  const [f, setF] = useState(() => EMPTY(prefill));
  const [config, setConfig] = useState(null);
  const [lookup, setLookup] = useState(null);
  const [looking, setLooking] = useState(false);
  const [slotLoad, setSlotLoad] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [denied, setDenied] = useState(false);
  const savingRef = useRef(false);
  const phoneRef = useRef(null);
  const startedAtRef = useRef(Date.now());
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    bookingApi.config().then(setConfig).catch((e) => {
      if (e?.response?.status === 403) { setDenied(true); } else { toast.error(formatApiError(e)); }
    });
    setTimeout(() => phoneRef.current?.focus(), 100);
  }, []);

  useEffect(() => {
    const digits = (f.customer_phone || "").replace(/[^0-9]/g, "");
    setF((s) => (s.customer_id ? { ...s, customer_id: null } : s));
    if (digits.length < 4) { setLookup(null); setLooking(false); return undefined; }
    setLooking(true);
    const h = setTimeout(async () => {
      try {
        const r = await bookingApi.lookup(f.customer_phone);
        setLookup(r);
        if (r?.found) {
          setF((s) => ({ ...s, customer_name: s.customer_name || r.name || "",
                         customer_surname: s.customer_surname || r.surname || "" }));
        }
      } catch { setLookup(null); } finally { setLooking(false); }
    }, 400);
    return () => clearTimeout(h);
  }, [f.customer_phone]);

  useEffect(() => {
    if (!f.date || !TIME_RE.test(f.time || "") || !f.pax) { return undefined; }
    const h = setTimeout(async () => {
      try { setSlotLoad(await bookingApi.slotLoad(f.date, f.time, f.pax)); } catch { setSlotLoad(null); }
    }, 300);
    return () => clearTimeout(h);
  }, [f.date, f.time, f.pax]);

  const origins = config?.origins || [];
  const quickTimes = config?.quick_times || ["20:00", "20:30", "21:00"];
  const timeOk = TIME_RE.test(f.time || "");
  const isPast = timeOk && f.date === todayISO() && f.time < romeNowHM();
  const ambiguous = Boolean(lookup?.ambiguous);
  const canSubmit = f.customer_name.trim() && f.customer_phone.trim().length >= 6
                    && Number.isInteger(f.pax) && f.pax > 0 && f.date && timeOk
                    && (!ambiguous || f.customer_id);
  const pickCandidate = (id) => {
    const c = (lookup?.candidates || []).find((x) => x.id === id);
    setF((s) => ({ ...s, customer_id: id, customer_name: c?.name || s.customer_name,
                   customer_surname: c?.surname || s.customer_surname }));
  };
  const red = slotLoad?.status === "red";

  const submit = async () => {
    if (!canSubmit || savingRef.current) { return; }
    savingRef.current = true; setSaving(true);
    try {
      const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000);
      const res = await bookingApi.create({ ...f, elapsed_seconds: elapsed, entry_point: entryPoint }, false);
      const msgLine = messageLine(res?.messaging);
      const fullName = [f.customer_name, f.customer_surname].filter(Boolean).join(" ");
      toast.success(`Prenotazione ${res.code} confermata`, {
        description: `${fullName} · ${f.pax} coperti · ${humanDateShort(f.date)} · ${f.time}${msgLine ? `\n${msgLine}` : ""}`,
      });
      if (onSaved) { onSaved(res); return; }
      setF(EMPTY()); setLookup(null); setSlotLoad(null); setFormKey((k) => k + 1);
      startedAtRef.current = Date.now();
      setTimeout(() => phoneRef.current?.focus(), 60);
    } catch (e) {
      if (e?.response?.status === 409) {
        const d = e?.response?.data?.detail || {};
        if (d.code === "customer_ambiguous") {
          setLookup({ found: false, ambiguous: true, candidates: d.candidates || [] });
          setF((s) => ({ ...s, customer_id: null }));
        }
        toast.error(d.message || "Fascia oltre il limite. Serve autorizzazione supervisore.");
      } else { toast.error(formatApiError(e)); }
    } finally { savingRef.current = false; setSaving(false); }
  };

  if (denied) {
    return (
      <div className="max-w-md mx-auto px-4 pt-16 text-center" data-testid="booking-access-denied">
        <h2 className="font-serif text-3xl text-ink-900">Accesso non autorizzato</h2>
        <p className="text-sm text-ink-500 mt-2">Il tuo profilo non ha il permesso di inserire prenotazioni. Contatta un responsabile.</p>
      </div>
    );
  }

  return (
    <div className="max-w-md md:max-w-5xl mx-auto px-3 sm:px-4 pt-3 md:pt-5" data-testid="booking-new-page" key={formKey}>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
        <div className="space-y-3">
          <Card>
            {origins.length > 0 && <OriginPicker origins={origins} value={f.booking_source} onChange={set("booking_source")} />}
            <div>
              <Label>Telefono</Label>
              <input ref={phoneRef} type="tel" inputMode="tel" autoComplete="tel" value={f.customer_phone}
                     onChange={(e) => set("customer_phone")(e.target.value)} placeholder="+39 …"
                     className={`${inputCls} h-14 text-2xl font-medium tabular-nums tracking-tight`} data-testid="booking-phone" />
            </div>
            <BookingCustomerCard card={lookup} loading={looking && !lookup} />
            {ambiguous && <AmbiguousPicker candidates={lookup.candidates} value={f.customer_id} onChange={pickCandidate} />}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Nome</Label>
                <input value={f.customer_name} autoComplete="given-name" autoCapitalize="words"
                       onChange={(e) => set("customer_name")(e.target.value)} className={inputCls} data-testid="booking-name" />
              </div>
              <div>
                <Label>Cognome</Label>
                <input value={f.customer_surname} autoComplete="family-name" autoCapitalize="words"
                       onChange={(e) => set("customer_surname")(e.target.value)} className={inputCls} data-testid="booking-surname" />
              </div>
            </div>
          </Card>
          <Card className="hidden md:block">
            <NotesField value={f.notes} onChange={set("notes")} testid="booking-notes" />
          </Card>
        </div>

        <div className="space-y-3">
          <Card>
            <div><Label>Zona</Label><ZonePicker value={f.zone} onChange={set("zone")} /></div>
            <div><Label>Coperti</Label><PaxStepper value={f.pax} onChange={set("pax")} /></div>
            <div><Label>Data</Label><DatePicker value={f.date} onChange={set("date")} /></div>
            <div>
              <Label>Orario</Label>
              <ReservationsTimePicker value={f.time} quickTimes={quickTimes} onChange={set("time")} isPast={isPast} />
            </div>
            <BookingSlotIndicator load={timeOk ? slotLoad : null} onPickSuggestion={set("time")} />
          </Card>
          <Card className="md:hidden">
            <NotesField value={f.notes} onChange={set("notes")} testid="booking-notes-mobile" />
          </Card>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 -mx-3 sm:-mx-4 mt-3 px-3 sm:px-4 pt-3
                      pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-sand-100 via-sand-100/95 to-transparent">
        <div className="md:max-w-md md:ml-auto">
          <WhatsAppConfirmToggle availability={config?.whatsapp_confirm} checked={f.send_whatsapp}
                                 onChange={set("send_whatsapp")} />
          <button type="button" disabled={!canSubmit || saving} onClick={submit} data-testid="booking-submit"
                  aria-busy={saving}
                  className={`w-full h-14 rounded-full text-white text-base font-medium tracking-wide flex items-center justify-center gap-2
                              shadow-lg transition-[background-color,opacity,transform] active:scale-[0.99]
                              ${(!canSubmit || saving) ? "bg-ink-500 cursor-not-allowed" : red ? "bg-rose-700 hover:bg-rose-800" : "bg-ink-900"}`}>
            {saving ? <><Loader2 className="w-5 h-5 animate-spin" /> Salvataggio…</>
              : <><Check className="w-5 h-5" /> {red ? "Conferma comunque" : "Conferma prenotazione"}</>}
          </button>
          {red && (
            <p className="text-xs text-ink-500 text-center mt-1.5" data-testid="booking-red-hint">
              Fascia oltre il limite: serve l'autorizzazione di un supervisore.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function NotesField({ value, onChange, testid }) {
  return (
    <div>
      <Label>Note prenotazione</Label>
      <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)}
                placeholder="Compleanno, torta, cane, seggiolone…" data-testid={testid}
                className="w-full p-3 rounded-xl bg-white border border-sand-300 text-base focus:outline-none focus:border-bronze-500 focus:ring-2 focus:ring-bronze-400/30" />
      <p className="text-[11px] text-ink-500 mt-1">Allergie e preferenze stabili le compila il cliente dal link WhatsApp.</p>
    </div>
  );
}
